import { arrayMove } from "@dnd-kit/sortable";
import { resolveMediaUrl } from "@utils/media/mediaUrl";

export function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

export function toPreviewText(value = "", max = 20) {
  const plain = String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return "";
  return plain.length > max ? `${plain.slice(0, max)}...` : plain;
}

export function hasRichTextContent(value = "") {
  return Boolean(
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );
}

export function createLocalizedObject(isos = [], seed = {}) {
  return isos.reduce((acc, iso) => {
    acc[iso] = seed?.[iso] || "";
    return acc;
  }, {});
}

export function normalizeSeedSections(seedSections = [], isos = []) {
  return seedSections.map((section, sectionIndex) => ({
    id: section?.id || `before-you-go-section-${sectionIndex + 1}`,
    itemType: section?.itemType || "category",
    order: Number(section?.order) || sectionIndex + 1,
    title: createLocalizedObject(isos, section?.title),
    titleTranslationIds: createLocalizedObject(
      isos,
      section?.titleTranslationIds,
    ),
    iconName: section?.iconName || "star",
    imageUrl: section?.imageUrl || "",
    image: section?.imageUrl
      ? {
          id: `${section?.id || sectionIndex}-image`,
          url: resolveMediaUrl(section.imageUrl),
          mediaType: "image",
          mimeType: "image/*",
        }
      : null,
    descriptions: createLocalizedObject(isos, section?.descriptions),
    descriptionTranslationIds: createLocalizedObject(
      isos,
      section?.descriptionTranslationIds,
    ),
    items: (Array.isArray(section?.items) ? section.items : []).map(
      (item, itemIndex) => ({
        id: item?.id || `${section?.id || sectionIndex}-item-${itemIndex + 1}`,
        itemType: item?.itemType || "hotel",
        order: Number(item?.order) || itemIndex + 1,
        displayOrder: Number(item?.displayOrder) || itemIndex + 1,
        title: createLocalizedObject(isos, item?.title),
        titleTranslationIds: createLocalizedObject(
          isos,
          item?.titleTranslationIds,
        ),
        location: item?.location || "",
        description: createLocalizedObject(isos, item?.description),
        descriptionTranslationIds: createLocalizedObject(
          isos,
          item?.descriptionTranslationIds,
        ),
        rating:
          item?.rating === null || typeof item?.rating === "undefined"
            ? ""
            : String(item.rating),
        websiteUrl: item?.websiteUrl || "",
        imageUrl: item?.imageUrl || "",
        image: item?.imageUrl
          ? {
              id: `${item?.id || `${sectionIndex}-${itemIndex}`}-image`,
              url: resolveMediaUrl(item.imageUrl),
              mediaType: "image",
              mimeType: "image/*",
            }
          : null,
      }),
    ),
  }));
}

export function pickTourName(tour) {
  const names = tour?.name || tour?.description?.name || {};
  const possibleValues = [
    names?.en,
    names?.es,
    names?.pt,
    names?.fr,
    names?.de,
  ];
  for (const value of possibleValues) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value?.value && String(value.value).trim())
      return String(value.value).trim();
  }
  return tour?.cityName || "Tour";
}

export function createEmptyItem(
  sectionId,
  items = [],
  isos = [],
  itemType = "hotel",
) {
  const nextIndex = (Array.isArray(items) ? items.length : 0) + 1;
  return {
    id: `${sectionId}-item-${Date.now()}`,
    itemType,
    order: nextIndex,
    displayOrder: nextIndex,
    title: createLocalizedObject(isos),
    titleTranslationIds: createLocalizedObject(isos),
    location: "",
    description: createLocalizedObject(isos),
    descriptionTranslationIds: createLocalizedObject(isos),
    rating: "",
    websiteUrl: "",
    imageUrl: "",
    image: null,
  };
}

export function createEmptySection(sections = [], isos = []) {
  const nextIndex = (Array.isArray(sections) ? sections.length : 0) + 1;
  return {
    id: `before-you-go-section-${Date.now()}`,
    itemType: "category",
    order: nextIndex,
    title: createLocalizedObject(isos),
    titleTranslationIds: createLocalizedObject(isos),
    iconName: "star",
    imageUrl: "",
    image: null,
    descriptions: createLocalizedObject(isos),
    descriptionTranslationIds: createLocalizedObject(isos),
    items: [],
  };
}

export function reorderSectionsWithSequentialOrder(
  sections = [],
  oldIndex,
  newIndex,
) {
  return arrayMove(sections, oldIndex, newIndex).map((section, index) => ({
    ...section,
    order: index + 1,
  }));
}

export function reorderItemsWithSequentialOrder(items = [], oldIndex, newIndex) {
  return arrayMove(items, oldIndex, newIndex).map((item, index) => ({
    ...item,
    order: index + 1,
    displayOrder: index + 1,
  }));
}
